import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, concat, defer, filter, isObservable, map, of } from 'rxjs';

export const LANGS = ['am', 'en', 'ru'] as const;

/**
 * Emits the value of `key` for the CURRENT language now (if it is loaded) and again on every
 * language change. Each emission is computed synchronously from the service state, so the
 * last one always wins. This replaces `translate.get()` + `onLangChange`, which can deliver a
 * stale `am` payload after the `en` change event.
 */
export function translateLive<T>(translate: TranslateService, key: string): Observable<T> {
  return defer(() => {
    const current = translate.currentLang ? translate.translations[translate.currentLang] : undefined;
    return concat(current ? of(current) : EMPTY, translate.onLangChange.pipe(map((e) => e.translations)));
  }).pipe(
    map((translations) => translate.getParsedResult(translations, key)),
    filter((value) => value != null && value !== key && !isObservable(value)),
    map((value) => value as unknown as T)
  );
}

/** Same semantics as the components' buildLink(): am => route, else /<lang><route>. */
export function langLink(translate: TranslateService, route: string): string {
  const lang = translate.currentLang || 'am';
  if (lang === 'am') return route;
  return route === '/' ? `/${lang}` : `/${lang}${route}`;
}

/** '/en/about?x#y' -> '/about'; '/', '/home', '/en', '/ru/home' -> '/'. */
export function stripLangPath(url: string): string {
  const path = url.split(/[?#]/)[0];
  const segments = path.split('/').filter(Boolean);
  if (segments.length && (LANGS as readonly string[]).includes(segments[0])) segments.shift();
  if (segments.length === 1 && segments[0] === 'home') segments.shift();
  return '/' + segments.join('/');
}
